import {
  adminOrderSchema,
  adminProductSchema,
  customerSuggestionSchema,
  isValidNif,
  mediaVariant,
  quoteSchema,
  type AdminProduct,
  type CartLine,
  type CustomerLookup,
  type InPersonLine,
  type Quote,
} from '@growup/site-shared';
import { Autocomplete, Button, Card, Checkbox, Field, Input, Notice, Select } from '@growup/ui';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { z } from 'zod';

import { getJson, messageOf, send } from '../api';
import { centsToInput, formatEuros, parseEuros } from './labels';

interface SaleLine {
  variantId: string;
  label: string;
  quantity: number;
  personalization: { name: string | null; number: number | null } | null;
  /** Preco especial por unidade (F-013), ou `null` com o preco da loja. */
  special: { cents: number; reason: string } | null;
  /** Quota "Anual + camisola" (F-001): o tamanho da camisola. */
  jerseyVariantId: string | null;
}

/** O formulario do preco especial de uma linha, enquanto se escreve. */
interface SpecialDraft {
  index: number;
  price: string;
  reason: string;
}

/** Uma variante que se pode vender, como aparece na pesquisa de produtos. */
interface ProductOption {
  id: string;
  product: AdminProduct;
  label: string;
  stock: number | null;
  /** As palavras do nome, variante, SKU e slug, sem acentos nem maiusculas. */
  words: string[];
}

/** Quantas sugestoes a pesquisa de produtos mostra de cada vez. */
const PRODUCT_SUGGESTIONS = 50;

const wordsOf = (text: string) =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

/** A capa do produto em pequeno, ou a inicial quando nao tem fotos. */
function ProductThumb({ product }: { product: AdminProduct }) {
  const cover = product.imageUrl ?? product.images[0];
  return cover ? (
    <img
      src={mediaVariant(cover, 'sm')}
      alt=""
      loading="lazy"
      className="size-10 shrink-0 border border-gu-line object-cover"
    />
  ) : (
    <span
      aria-hidden="true"
      className="flex size-10 shrink-0 items-center justify-center border border-gu-line bg-gu-ink/10 font-semibold text-gu-ink-muted"
    >
      {product.name.charAt(0).toUpperCase()}
    </span>
  );
}

function ProductOptionRow({ option }: { option: ProductOption }) {
  return (
    <span className="flex items-center gap-3">
      <ProductThumb product={option.product} />
      <span className="min-w-0 flex-1 truncate">{option.label}</span>
      <span className="flex shrink-0 flex-col items-end text-caption">
        <span className="font-semibold">{formatEuros(option.product.priceCents)}</span>
        {option.stock === null ? null : option.stock > 0 ? (
          <span className="text-gu-ink-muted">{option.stock} em stock</span>
        ) : (
          <span className="text-gu-danger">Esgotado</span>
        )}
      </span>
    </span>
  );
}

const LEVEL_LABEL = {
  guest: 'Sem conta',
  user: 'Utilizador',
  member: 'Membro',
  associate: 'Associado',
};

/**
 * In-person sale (RN-E2, RN-E4): the same stock and prices as the site. The
 * customer's account is optional; without one there is no associate discount
 * and no membership fee. A line can take a lower special price with a reason
 * (event prices, F-013); then it gets no associate discount.
 */
export function SaleView({ onSold }: { onSold: (orderId: string) => void }) {
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [identifier, setIdentifier] = useState('');
  const [customer, setCustomer] = useState<CustomerLookup | null>(null);
  const [lines, setLines] = useState<SaleLine[]>([]);
  const [variantId, setVariantId] = useState('');
  const [productQuery, setProductQuery] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [persName, setPersName] = useState('');
  const [persNumber, setPersNumber] = useState('');
  const [jerseySize, setJerseySize] = useState('');
  const [method, setMethod] = useState<'cash' | 'mbway'>('cash');
  const [received, setReceived] = useState(true);
  const [nif, setNif] = useState('');
  const [billingName, setBillingName] = useState('');
  const [giftCode, setGiftCode] = useState('');
  const [quote, setQuote] = useState<Quote | null>(null);
  const [draft, setDraft] = useState<SpecialDraft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getJson('/api/site/store/products', z.array(adminProductSchema))
      // As camisolas de streamer e de equipa podem estar fora da loja: ao balcao
      // entram com o codigo.
      .then((list) => setProducts(list.filter((p) => p.active || p.streamerGift || p.teamShirt)))
      .catch((e: unknown) => setError(messageOf(e)));
  }, []);

  const options = useMemo(
    () =>
      products.flatMap((p) =>
        p.variants
          .filter((v) => v.active)
          .map((v): ProductOption => {
            const label = `${p.name}${v.label === 'Unico' ? '' : ` · ${v.label}`}`;
            return {
              id: v.id,
              product: p,
              label,
              stock: v.stock,
              words: wordsOf([label, v.sku ?? '', p.slug].join(' ')),
            };
          }),
      ),
    [products],
  );
  // Cada palavra escrita tem de comecar uma palavra do produto (ex.: "cam m", "cam-m").
  const searchProducts = (q: string) => {
    const typed = wordsOf(q);
    return Promise.resolve(
      options
        .filter((o) => typed.every((t) => o.words.some((w) => w.startsWith(t))))
        .slice(0, PRODUCT_SUGGESTIONS),
    );
  };
  const chosen = options.find((o) => o.id === variantId);
  const variantOf = (id: string) =>
    options.find((o) => o.id === id)?.product.variants.find((v) => v.id === id);
  // A quota "Anual + camisola": os tamanhos ativos da camisola escolhida no catalogo.
  const chosenVariant = chosen ? variantOf(chosen.id) : undefined;
  const jerseySizes =
    chosenVariant?.feePlan === 'annual_jersey'
      ? (products
          .find((p) => p.id === chosenVariant.jerseyProductId)
          ?.variants.filter((v) => v.active) ?? [])
      : null;

  const cartLines: CartLine[] = lines.map((l) => ({
    variantId: l.variantId,
    quantity: l.quantity,
    personalization: l.personalization,
    // Ao balcao a quota e de quem compra; a de um jovem regista-se na ficha dele.
    forAccountId: null,
    jerseyVariantId: l.jerseyVariantId,
  }));
  const linesKey = JSON.stringify(cartLines);

  // Only for stock: the quote prices for the signed-in account (the staff
  // member), so the total below is worked out from the customer's level.
  useEffect(() => {
    const parsed = JSON.parse(linesKey) as CartLine[];
    if (parsed.length === 0) return setQuote(null);
    send('POST', '/api/store/quote', { lines: parsed })
      .then((q) => setQuote(quoteSchema.parse(q)))
      .catch(() => setQuote(null));
  }, [linesKey]);

  const associate = customer?.level === 'associate';
  const productOf = (l: SaleLine) => options.find((o) => o.id === l.variantId)?.product;
  const unitCents = (l: SaleLine) => {
    const p = productOf(l);
    if (!p) return 0;
    const discount =
      associate && p.kind !== 'membership_fee'
        ? Math.round(
            ((variantOf(l.variantId)?.priceCents ?? p.priceCents) * p.associateDiscountPct) / 100,
          )
        : 0;
    const base = variantOf(l.variantId)?.priceCents ?? p.priceCents;
    const pers = l.personalization ? p.personalizationPriceCents : 0;
    return (l.special ? l.special.cents : base - discount) + pers;
  };
  const estimate = lines.reduce((sum, l) => sum + l.quantity * unitCents(l), 0);
  // Um codigo de oferta tira uma unidade da camisola de streamer ou de equipa (a API confirma).
  const giftProduct = giftCode.trim()
    ? lines
        .map((l) => options.find((o) => o.id === l.variantId)?.product)
        .find((p) => p?.streamerGift || p?.teamShirt)
    : undefined;
  const total = Math.max(0, estimate - (giftProduct?.priceCents ?? 0));

  const searchCustomers = (q: string) =>
    getJson(
      `/api/site/store/customers/suggestions?q=${encodeURIComponent(q)}`,
      z.array(customerSuggestionSchema),
    );

  const addLine = (e: FormEvent) => {
    e.preventDefault();
    if (!chosen) return;
    const size = jerseySizes?.find((v) => v.id === jerseySize);
    if (jerseySizes && !size) return setError('Escolha o tamanho da camisola.');
    const n = Math.max(1, Math.min(20, Number(quantity) || 1));
    const personalization =
      chosen.product.personalization && (persName.trim() || persNumber.trim())
        ? {
            name: persName.trim() ? persName.trim().toUpperCase() : null,
            number: persNumber.trim() ? Number(persNumber) : null,
          }
        : null;
    setLines([
      ...lines,
      {
        variantId: chosen.id,
        label: size ? `${chosen.label} (${size.label})` : chosen.label,
        quantity: chosen.product.kind === 'membership_fee' ? 1 : n,
        personalization,
        special: null,
        jerseyVariantId: size?.id ?? null,
      },
    ]);
    setVariantId('');
    setJerseySize('');
    setProductQuery('');
    setQuantity('1');
    setPersName('');
    setPersNumber('');
  };

  const draftProduct = draft ? productOf(lines[draft.index] as SaleLine) : undefined;
  const draftCents = draft ? parseEuros(draft.price) : null;
  const draftProblem = !draft
    ? null
    : draftCents === null
      ? 'Escreva o preço por unidade (ex.: 30 ou 29,50).'
      : draftProduct && draftCents >= draftProduct.priceCents
        ? `Só pode baixar: tem de ser menos de ${formatEuros(draftProduct.priceCents)}.`
        : !draft.reason.trim()
          ? 'Escreva o motivo (ex.: Preço evento).'
          : null;
  const applySpecial = (e: FormEvent) => {
    e.preventDefault();
    if (!draft || draftProblem || draftCents === null) return;
    setLines(
      lines.map((l, i) =>
        i === draft.index
          ? { ...l, special: { cents: draftCents, reason: draft.reason.trim() } }
          : l,
      ),
    );
    setDraft(null);
  };

  const sell = async () => {
    if (nif.trim() && !isValidNif(nif.trim())) return setError('NIF inválido.');
    setBusy(true);
    setError(null);
    try {
      const order = adminOrderSchema.parse(
        await send('POST', '/api/site/store/orders', {
          customerAccountId: customer?.accountId ?? null,
          lines: lines.map((l, i): InPersonLine => ({
            ...(cartLines[i] as CartLine),
            specialPrice: l.special
              ? { unitPriceCents: l.special.cents, reason: l.special.reason }
              : null,
          })),
          giftCode: customer && giftCode.trim() ? giftCode.trim() : null,
          paymentMethod: method,
          received,
          billing: { name: billingName || null, nif: nif.trim() || null },
        }),
      );
      setLines([]);
      setDraft(null);
      setCustomer(null);
      setIdentifier('');
      setNif('');
      setBillingName('');
      setGiftCode('');
      onSold(order.id);
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
      <Card headingLevel={2} title="Venda ao balcão">
        <div className="flex flex-col gap-4">
          <Field
            label="Cliente (login ou email)"
            optional
            hint="Sem conta: sem desconto de associado e sem quota."
          >
            <Autocomplete
              value={identifier}
              onChange={(v) => {
                setIdentifier(v);
                setCustomer(null);
              }}
              search={searchCustomers}
              getKey={(c) => c.accountId}
              renderItem={(c) => (
                <span className="flex items-center justify-between gap-3">
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate font-semibold">{c.login}</span>
                    <span className="truncate text-caption text-gu-ink-muted">{c.email}</span>
                  </span>
                  <span className="text-caption">{LEVEL_LABEL[c.level]}</span>
                </span>
              )}
              onSelect={(c) => {
                setCustomer(c);
                setIdentifier(c.login);
              }}
            />
          </Field>
          {customer ? (
            <p className="text-body">
              <span className="font-semibold">{customer.login}</span> ·{' '}
              {LEVEL_LABEL[customer.level]}{' '}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setCustomer(null);
                  setIdentifier('');
                }}
              >
                Tirar
              </Button>
            </p>
          ) : null}

          <form className="flex flex-col gap-3" onSubmit={addLine}>
            <Field label="Produto" hint="Escreva o nome ou o SKU; setas e Enter para escolher.">
              <Autocomplete
                // Os produtos chegam depois: volta a procurar quando a lista muda.
                key={options.length}
                value={productQuery}
                onChange={(v) => {
                  setProductQuery(v);
                  setVariantId('');
                }}
                search={searchProducts}
                onSelect={(o) => {
                  setVariantId(o.id);
                  setProductQuery(o.label);
                }}
                getKey={(o) => o.id}
                renderItem={(o) => <ProductOptionRow option={o} />}
                minChars={0}
                delayMs={0}
                emptyText="Nenhum produto com esse nome ou SKU."
                placeholder={options.length > 0 ? 'Procurar produto…' : 'A carregar produtos…'}
                disabled={options.length === 0}
              />
            </Field>
            {chosen ? (
              <div className="border-2 border-gu-line px-3 py-2 text-body">
                <ProductOptionRow option={chosen} />
              </div>
            ) : null}
            {jerseySizes ? (
              <Field
                label="Tamanho da camisola"
                hint="A encomenda fica Paga até a marcar entregue."
              >
                <Select value={jerseySize} onChange={(e) => setJerseySize(e.target.value)}>
                  <option value="">Escolha…</option>
                  {jerseySizes.map((v) => (
                    <option key={v.id} value={v.id} disabled={v.stock !== null && v.stock <= 0}>
                      {v.label}
                      {v.stock !== null ? ` (${String(v.stock)} un.)` : ''}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null}
            <div className="grid grid-cols-[6rem_minmax(0,1fr)_6rem] items-end gap-2">
              <Field label="Qtd.">
                <Input
                  inputMode="numeric"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                />
              </Field>
              {chosen?.product.personalization ? (
                <>
                  <Field label="Nome" optional>
                    <Input
                      value={persName}
                      maxLength={12}
                      onChange={(e) => setPersName(e.target.value)}
                    />
                  </Field>
                  <Field label="Número" optional>
                    <Input
                      inputMode="numeric"
                      value={persNumber}
                      maxLength={2}
                      onChange={(e) => setPersNumber(e.target.value.replace(/\D/g, ''))}
                    />
                  </Field>
                </>
              ) : null}
            </div>
            <div>
              <Button type="submit" variant="secondary" disabled={!chosen}>
                Acrescentar
              </Button>
            </div>
          </form>
        </div>
      </Card>

      <Card headingLevel={2} title="Talão">
        <div className="flex flex-col gap-4">
          {lines.length === 0 ? <p className="text-gu-ink-muted">Ainda sem artigos.</p> : null}
          <ul className="flex flex-col divide-y divide-gu-line">
            {lines.map((l, i) => {
              const fee = productOf(l)?.kind === 'membership_fee';
              return (
                <li key={i} className="flex flex-col gap-2 py-2 text-body">
                  <div className="flex items-center justify-between gap-2">
                    <span>
                      {l.quantity} × {l.label}
                      {l.personalization ? (
                        <span className="block text-caption text-gu-ink-muted">
                          {[l.personalization.name, l.personalization.number]
                            .filter((v) => v !== null)
                            .join(' ')}
                        </span>
                      ) : null}
                      {l.special ? (
                        <span className="block text-caption text-gu-ink-muted">
                          Preço especial {formatEuros(l.special.cents)}/un. · {l.special.reason}
                        </span>
                      ) : null}
                    </span>
                    <span className="flex shrink-0 items-center gap-1">
                      <span className="mr-2">{formatEuros(l.quantity * unitCents(l))}</span>
                      {!fee && draft?.index !== i ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            setDraft({
                              index: i,
                              price: l.special ? centsToInput(l.special.cents) : '',
                              reason: l.special?.reason ?? '',
                            })
                          }
                        >
                          {l.special ? 'Mudar preço' : 'Preço especial'}
                        </Button>
                      ) : null}
                      {l.special ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            setLines(lines.map((x, j) => (j === i ? { ...x, special: null } : x)))
                          }
                        >
                          Preço normal
                        </Button>
                      ) : null}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setLines(lines.filter((_, j) => j !== i));
                          setDraft(null);
                        }}
                      >
                        Tirar
                      </Button>
                    </span>
                  </div>
                  {draft?.index === i ? (
                    <form
                      className="grid grid-cols-[8rem_minmax(0,1fr)] items-end gap-2"
                      onSubmit={applySpecial}
                    >
                      <Field label="Preço por unidade (€)">
                        <Input
                          inputMode="decimal"
                          value={draft.price}
                          onChange={(e) => setDraft({ ...draft, price: e.target.value })}
                        />
                      </Field>
                      <Field label="Motivo">
                        <Input
                          value={draft.reason}
                          maxLength={80}
                          placeholder="Preço evento"
                          onChange={(e) => setDraft({ ...draft, reason: e.target.value })}
                        />
                      </Field>
                      <p className="col-span-2 text-caption text-gu-ink-muted">
                        {draftProblem ??
                          'Só nesta venda. Com preço especial não há desconto de associado.'}
                      </p>
                      <div className="col-span-2 flex gap-2">
                        <Button type="submit" size="sm" disabled={draftProblem !== null}>
                          Aplicar
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => setDraft(null)}
                        >
                          Cancelar
                        </Button>
                      </div>
                    </form>
                  ) : null}
                </li>
              );
            })}
          </ul>
          {lines.length > 0 ? (
            <p className="text-right font-semibold">
              Total {formatEuros(total)}
              {giftProduct ? (
                <span className="block text-caption font-normal text-gu-ink-muted">
                  com a oferta de {giftProduct.name}
                </span>
              ) : null}
              {associate && lines.some((l) => !l.special) ? (
                <span className="block text-caption font-normal text-gu-ink-muted">
                  com desconto de associado
                </span>
              ) : null}
              {lines.some((l) => l.special) ? (
                <span className="block text-caption font-normal text-gu-ink-muted">
                  com preço especial
                </span>
              ) : null}
            </p>
          ) : null}
          {quote?.problems.some(
            (p) => p.problem === 'out_of_stock' || p.problem === 'unavailable',
          ) ? (
            <Notice tone="warning">
              Há artigos sem stock ou indisponíveis; a venda vai ser recusada.
            </Notice>
          ) : null}
          {customer ? (
            <Field
              label="Código de oferta"
              optional
              hint="Do cliente escolhido. Confirma-se ao registar a venda."
            >
              <Input
                value={giftCode}
                autoCapitalize="characters"
                onChange={(e) => setGiftCode(e.target.value)}
              />
            </Field>
          ) : null}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Pagamento">
              <Select
                value={method}
                onChange={(e) => setMethod(e.target.value as 'cash' | 'mbway')}
              >
                <option value="cash">Dinheiro</option>
                <option value="mbway">MB WAY</option>
              </Select>
            </Field>
            <Field label="NIF" optional>
              <Input inputMode="numeric" value={nif} onChange={(e) => setNif(e.target.value)} />
            </Field>
          </div>
          {nif.trim() ? (
            <Field label="Nome na fatura" optional>
              <Input value={billingName} onChange={(e) => setBillingName(e.target.value)} />
            </Field>
          ) : null}
          <Checkbox
            label="Pagamento já recebido"
            hint="Sem isto, a encomenda fica por pagar e o stock reservado até marcar recebido."
            checked={received}
            onChange={(e) => setReceived(e.target.checked)}
          />
          {error ? <Notice tone="danger">{error}</Notice> : null}
          <div>
            <Button disabled={busy || lines.length === 0} onClick={() => void sell()}>
              Registar venda
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
